import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, PlayCircle, ArrowUpRight, X, ChevronUp } from "lucide-react";
import {
  handleJournalClick,
  handleTeamClick,
  fetchInstagramPosts,
  fetchRecentCount,
  gaEvent,
} from "@/lib/tracking";
import CommunityResults from "@/components/CommunityResults";

const HERO_PHOTO_URL =
  "https://customer-assets.emergentagent.com/job_putrade-community/artifacts/ohpndxl1_poza%20hero%20section.jpg";

// Timing constants (ms)
const BONUS_BANNER_DELAY_MS = 12000;
const COOKIE_POPUP_DELAY_MS = 600;
const STATS_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/* ---------- Monochrome social icons ---------- */
const TikTokIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 256 256" className={className} fill="currentColor" aria-hidden>
    <path d="M168 26h-26v140a30 30 0 1 1-30-30v-26a56 56 0 1 0 56 56V96a72 72 0 0 0 44 15V85a46 46 0 0 1-44-46Z" />
  </svg>
);
const InstagramIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);
const FacebookIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M22.675 0H1.325C.593 0 0 .593 0 1.326v21.348C0 23.408.593 24 1.325 24h11.494v-9.294H9.691v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .593 23.406 0 22.675 0Z" />
  </svg>
);
const YouTubeIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
  </svg>
);

const HERO_SOCIALS = [
  { key: "tiktok", Icon: TikTokIcon, url: "https://www.tiktok.com/@ceogabriel", event: "tiktok_click", label: "TikTok" },
  { key: "instagram", Icon: InstagramIcon, url: "https://www.instagram.com/ceogabrielmihalache", event: "instagram_click", label: "Instagram" },
  { key: "facebook", Icon: FacebookIcon, url: "https://www.facebook.com/ceogabrielmihalache", event: "facebook_click", label: "Facebook" },
  { key: "youtube", Icon: YouTubeIcon, url: "https://youtube.com/@ceogabriel", event: "youtube_click", label: "YouTube" },
];

const FOLLOWERS_PLATFORMS = [
  { key: "tiktok", Icon: TikTokIcon, name: "TikTok", count: "277.188", url: "https://www.tiktok.com/@ceogabriel", event: "tiktok_click" },
  { key: "instagram", Icon: InstagramIcon, name: "Instagram", count: "71.300", url: "https://www.instagram.com/ceogabrielmihalache", event: "instagram_click" },
  { key: "facebook", Icon: FacebookIcon, name: "Facebook", count: "52.000", url: "https://www.facebook.com/ceogabrielmihalache", event: "facebook_click" },
  { key: "youtube", Icon: YouTubeIcon, name: "YouTube", count: "50.300", url: "https://youtube.com/@ceogabriel", event: "youtube_click" },
];

/* ---------- Hero ---------- */
const Hero = ({ onOpenFollowers }) => (
  <section data-testid="hub-hero" className="relative">
    <div className="absolute inset-0 overflow-hidden">
      <img
        src={HERO_PHOTO_URL}
        alt="ceogabriel"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "center 22%" }}
        loading="eager"
        decoding="async"
        fetchPriority="high"
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 35%, rgba(0,0,0,0.75) 75%, #ffffff 100%)",
        }}
      />
    </div>

    <div className="relative px-6 pt-12 pb-10 sm:pt-14 sm:pb-12 min-h-[58vh] sm:min-h-[56vh] flex flex-col justify-end">
      <div className="max-w-md mx-auto w-full text-center">
        <h1
          data-testid="hub-title"
          className="font-display font-extrabold tracking-tight text-white text-[2.6rem] leading-[1.05] sm:text-5xl"
          style={{ textShadow: "0 2px 24px rgba(0,0,0,0.45)" }}
        >
          Payouts By Ceo
        </h1>
        <p className="mt-3 text-[0.95rem] sm:text-base text-white/85 font-medium">
          Trading <span className="text-white/40 mx-1">·</span> Life{" "}
          <span className="text-white/40 mx-1">·</span> Comunitate
        </p>
        <p className="mt-1.5 text-xs sm:text-[13px] text-white/55 tracking-[0.18em] uppercase">
          by ceogabriel
        </p>

        {/* Followers counter */}
        <button
          type="button"
          onClick={onOpenFollowers}
          data-testid="followers-counter-btn"
          className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-[12.5px] font-semibold transition"
        >
          <span className="text-[#E8C77A]">450K+</span>
          <span className="text-white/85">followers</span>
          <ChevronUp className="w-3.5 h-3.5 text-white/60 -mr-0.5" />
        </button>

        {/* Social icons (slightly bigger) */}
        <div className="mt-5 flex items-center justify-center gap-6">
          {HERO_SOCIALS.map((s) => (
            <a
              key={s.key}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`hero-social-${s.key}`}
              onClick={() => gaEvent(s.event, { source: "hero" })}
              aria-label={s.label}
              className="text-white/90 hover:text-white transition-colors"
            >
              <s.Icon className="w-7 h-7" />
            </a>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Main CTA cards ---------- */
const SocialProof = ({ count, label, testId }) => {
  if (!count || count <= 0) return null;
  return (
    <p
      data-testid={testId}
      className="mt-2 px-1 text-center text-[11.5px] text-black/55 leading-snug"
    >
      <span className="font-semibold text-black/80">{count}</span> {label}
    </p>
  );
};

const MainButtons = ({ journalCount, teamCount }) => (
  <section data-testid="main-buttons" className="px-5 -mt-2 relative z-10">
    <div className="max-w-md mx-auto space-y-3.5">
      {/* CEO's Journal (Telegram) */}
      <div>
        <button
          type="button"
          onClick={() => handleJournalClick("hub_main")}
          data-testid="cta-journal"
          className="group w-full min-h-[96px] flex items-center justify-between gap-3 px-6 py-5 rounded-2xl bg-black text-white shadow-[0_8px_24px_-8px_rgba(0,0,0,0.35)] hover:shadow-[0_14px_30px_-10px_rgba(0,0,0,0.45)] hover:-translate-y-[1px] transition-all duration-300 border border-black"
        >
          <div className="text-left min-w-0">
            <p className="font-display text-[15px] sm:text-base font-bold leading-tight">
              CEO&#39;s Journal
            </p>
            <p className="mt-1 text-[12px] sm:text-[13px] text-white/60 font-medium leading-snug">
              Locul în care îmi împărtășesc gândurile despre piață, analize, experiențe și lecțiile învățate de-a lungul timpului.
            </p>
          </div>
          <ArrowUpRight className="w-5 h-5 text-white/70 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform flex-shrink-0" />
        </button>
        <SocialProof
          count={journalCount}
          label="persoane au accesat jurnalul în ultimele 24h"
          testId="proof-journal"
        />
      </div>

      {/* Payouts By CEO | Team — gold accent, pulse-shake every 4s */}
      <div>
        <div className="pu-pulse-shake">
          <button
            type="button"
            onClick={() => handleTeamClick("hub_main")}
            data-testid="cta-team"
            className="group w-full min-h-[96px] flex items-center justify-between gap-3 px-6 py-5 rounded-2xl bg-white text-black border border-black/10 shadow-[0_8px_24px_-10px_rgba(17,24,39,0.18)] hover:shadow-[0_14px_30px_-10px_rgba(17,24,39,0.22)] hover:-translate-y-[1px] transition-all duration-300 relative overflow-hidden"
          >
            <span
              aria-hidden
              className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#C9A24A]"
            />
            <div className="text-left min-w-0">
              <p className="font-display text-[15px] sm:text-base font-bold leading-tight">
                Payouts By CEO | Team
              </p>
              <p className="mt-1 text-[12px] sm:text-[13px] text-black/55 font-medium leading-snug">
                Ia legătura cu un membru al echipei noastre pentru a obține acces în Trading Community și pentru a beneficia de semnale automate, analize de piață și toate resursele necesare pentru a evolua ca trader.
              </p>
            </div>
            <ArrowUpRight className="w-5 h-5 text-[#C9A24A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform flex-shrink-0" />
          </button>
        </div>
        <SocialProof
          count={teamCount}
          label="persoane au contactat echipa în ultimele 24h"
          testId="proof-team"
        />
      </div>
    </div>
  </section>
);

/* ---------- Instagram carousel ---------- */
const InstagramCarousel = () => {
  const scrollerRef = useRef(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchInstagramPosts(5)
        .then((data) => {
          if (cancelled) return;
          setPosts(data?.posts && data.posts.length > 0 ? data.posts : []);
        })
        .catch(() => {
          if (!cancelled) setPosts([]);
        });
    };
    load();
    // Refresh every 10 minutes so new posts appear automatically.
    const id = setInterval(load, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Graceful hidden state: if we have no real posts (e.g. token expired),
  // don't render placeholders — just hide the whole section.
  if (!posts || posts.length === 0) return null;

  return (
    <section data-testid="instagram-section" className="px-5 mt-12 sm:mt-14">
      <div className="max-w-md mx-auto">
        <h2 className="font-display text-lg sm:text-xl font-extrabold text-black tracking-tight">
          Ultimele postări
        </h2>
        <p className="mt-1.5 text-[13px] text-black/55 leading-relaxed">
          Urmărește ultimele update-uri, clipuri informaționale și challenge-uri de pe{" "}
          <a
            href="https://www.instagram.com/payoutsbyceo"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => gaEvent("instagram_click", { source: "ig_section_handle" })}
            data-testid="ig-handle-link"
            className="font-semibold text-black underline decoration-[#C9A24A] decoration-2 underline-offset-2 hover:decoration-black transition"
          >
            @payoutsbyceo
          </a>
          .
        </p>

        <div
          ref={scrollerRef}
          data-testid="instagram-carousel"
          className="mt-5 flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-1"
          style={{ scrollbarWidth: "none" }}
        >
          {posts.map((p) => {
            const img =
              p.media_type === "VIDEO" ? p.thumbnail_url : p.media_url || p.thumbnail_url;
            return (
              <a
                key={p.id}
                href={p.permalink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`instagram-post-${p.id}`}
                onClick={() => gaEvent("instagram_post_click", { post_id: p.id })}
                className="snap-start shrink-0 w-[62%] sm:w-[44%] rounded-xl overflow-hidden border border-black/10 bg-neutral-50 hover:-translate-y-[1px] transition-transform duration-300"
              >
                <div className="relative aspect-square bg-neutral-100">
                  {img ? (
                    <img
                      src={img}
                      alt="@payoutsbyceo"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-black/30 text-[10px] font-bold uppercase tracking-widest">
                      @payoutsbyceo
                    </div>
                  )}
                  {p.media_type === "VIDEO" && (
                    <span className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-black/65 flex items-center justify-center">
                      <PlayCircle className="w-4 h-4 text-white" />
                    </span>
                  )}
                </div>
                <div className="px-3 py-2.5 flex items-center justify-center bg-white">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-black">
                    Vezi pe Instagram
                    <ChevronRight className="inline w-3 h-3 ml-1 -mt-0.5" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- Followers bottom sheet ---------- */
const FollowersSheet = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
      data-testid="followers-sheet"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 smart-blur-light animate-fade-up"
        onClick={onClose}
      />
      <div className="relative w-full sm:max-w-md mx-auto bg-white rounded-t-3xl sm:rounded-3xl shadow-[0_-20px_60px_-12px_rgba(0,0,0,0.35)] p-6 sm:p-7 animate-sheet-up">
        <div className="mx-auto w-12 h-1 rounded-full bg-black/10 sm:hidden mb-4" />
        <div className="flex items-start justify-between gap-3 mb-4">
          <h3 className="font-display text-xl font-extrabold text-black tracking-tight">
            Comunitatea ceogabriel
          </h3>
          <button
            onClick={onClose}
            aria-label="Închide"
            data-testid="followers-sheet-close"
            className="w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 text-black/70 flex items-center justify-center transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <ul className="space-y-2.5">
          {FOLLOWERS_PLATFORMS.map((p) => (
            <li key={p.key}>
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`followers-row-${p.key}`}
                onClick={() => gaEvent(p.event, { source: "followers_sheet" })}
                className="flex items-center gap-3 px-4 py-3 rounded-xl border border-black/10 bg-white hover:bg-neutral-50 transition"
              >
                <span className="w-9 h-9 rounded-lg bg-black text-white flex items-center justify-center">
                  <p.Icon className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-[15px] font-extrabold text-black leading-tight">
                    {p.name}
                  </p>
                  <p className="text-[12.5px] text-black/55 font-medium">
                    {p.count} followers
                  </p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-black/40" />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ---------- Footer ---------- */
const Footer = () => (
  <footer data-testid="hub-footer" className="px-5 pb-10 pt-14">
    <div className="max-w-md mx-auto text-center">
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[11px] font-semibold text-black/55">
        <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-black transition">Privacy Policy</Link>
        <span className="text-black/20">·</span>
        <Link to="/terms-and-conditions" data-testid="footer-terms" className="hover:text-black transition">Terms &amp; Conditions</Link>
        <span className="text-black/20">·</span>
        <Link to="/gdpr-cookies" data-testid="footer-gdpr" className="hover:text-black transition">GDPR / Cookies</Link>
        <span className="text-black/20">·</span>
        <Link to="/risk-disclaimer" data-testid="footer-risk" className="hover:text-black transition">Risk Disclaimer</Link>
      </div>
      <p className="mt-5 text-[11px] text-black/50 leading-relaxed max-w-sm mx-auto">
        Tradingul implică risc și poate duce la pierderea capitalului investit.
        Informațiile prezentate sunt educative și nu reprezintă consultanță
        financiară sau promisiune de profit.
      </p>
      <p className="mt-4 text-[11px] text-black/40">© Payouts By Ceo</p>
    </div>
  </footer>
);

/* ---------- Page ---------- */
export default function LinkHubPage() {
  const [followersOpen, setFollowersOpen] = useState(false);
  const [journalCount, setJournalCount] = useState(null);
  const [teamCount, setTeamCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchRecentCount("ceos_journal_click", 24).then(
        (c) => !cancelled && setJournalCount(c)
      );
      fetchRecentCount("team_telegram_click", 24).then(
        (c) => !cancelled && setTeamCount(c)
      );
    };
    load();
    // Refresh every 5 minutes while the tab is open.
    const id = setInterval(load, STATS_REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <div data-testid="link-hub-page" className="min-h-screen bg-white text-black antialiased">
      <Hero
        onOpenFollowers={() => {
          gaEvent("followers_counter_click");
          setFollowersOpen(true);
        }}
      />
      <MainButtons journalCount={journalCount} teamCount={teamCount} />
      <CommunityResults />
      <InstagramCarousel />
      <Footer />

      <FollowersSheet open={followersOpen} onClose={() => setFollowersOpen(false)} />
    </div>
  );
}
